import { formatFailureShare } from "../ui/failureTone.js";
import { reliabilityTone, reliabilityWidth } from "../ui/reliabilityTone.js";

/**
 * ReliabilityReport
 *
 * The failure share of our polling, at three levels of the same measurement:
 * everything, by provider, by feed.
 *
 * **Why one component for the customer and the administrator.** The report is the
 * same shape from the same endpoint; what differs is that a customer sees their
 * own calendars and an administrator sees everybody's. Forking the component would
 * mean two places to fix the day the numbers are wrong, and the administrator's
 * copy is exactly the one nobody looks at until something has gone wrong.
 *
 * **Why the numbers are printed beside every bar.** The colour and the length are
 * judgements; the counts are the evidence. A customer asking "why is this red"
 * has to be able to answer it from the row itself, and "9 of 10 failed" is a
 * different situation from "900 of 1000 failed" even at the same share.
 */

const WINDOW_LABELS = {
  "1h": "1 hour",
  "6h": "6 hours",
  "24h": "24 hours",
  "7d": "7 days",
};

/** WINDOW_ORDER is the switcher's order: shortest first, as the eye reads a range. */
export const WINDOW_ORDER = ["1h", "6h", "24h", "7d"];

function shareLabel(rate) {
  return formatFailureShare(rate);
}

/** ShareBar is the visual share: a filled track, coloured by the same value. */
function ShareBar({ rate }) {
  const tone = reliabilityTone(rate);
  return (
    <span
      data-share-bar={rate}
      aria-hidden="true"
      style={{
        display: "block",
        position: "relative",
        height: 6,
        minWidth: 60,
        borderRadius: 3,
        background: "var(--surface-sunken, rgba(120, 130, 145, 0.18))",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          width: `${reliabilityWidth(rate)}%`,
          borderRadius: 3,
          background: tone,
        }}
      />
    </span>
  );
}

function Counters({ polls, failures, rate, tone }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8, whiteSpace: "nowrap" }}>
      <span style={{ color: tone, fontWeight: 600 }} data-failure-share={rate}>
        {shareLabel(rate)}
      </span>
      <span style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
        {failures.toLocaleString()} of {polls.toLocaleString()}
      </span>
    </span>
  );
}

function WindowSwitcher({ windowToken, onWindow }) {
  return (
    <span role="group" aria-label="Window" style={{ display: "inline-flex", gap: 4 }}>
      {WINDOW_ORDER.map((token) => {
        const active = token === windowToken;
        return (
          <button
            key={token}
            type="button"
            aria-pressed={active}
            data-window={token}
            onClick={() => onWindow?.(token)}
            style={{
              border: "1px solid var(--border-subtle, rgba(120,130,145,0.3))",
              background: active ? "var(--surface-sunken, rgba(120,130,145,0.18))" : "transparent",
              color: "inherit",
              fontWeight: active ? 600 : 400,
              fontSize: "var(--text-xs)",
              padding: "3px 8px",
              borderRadius: 999,
              cursor: onWindow ? "pointer" : "default",
            }}
          >
            {token}
          </button>
        );
      })}
    </span>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="ical-card">
      <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>{title}</p>
      {subtitle ? (
        <p style={{ margin: "4px 0 0", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          {subtitle}
        </p>
      ) : null}
      {children}
    </div>
  );
}

export default function ReliabilityReport({
  report,
  windowToken,
  onWindow,
  scope = "project",
  loading = false,
  refreshing = false,
  error = null,
  onOpenFeed,
}) {
  const activeWindow = windowToken ?? report?.window ?? "24h";
  const label = WINDOW_LABELS[activeWindow] ?? activeWindow;

  if (error) {
    return (
      <Card title={`Reliability · last ${label}`}>
        <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          {error.message ?? "The reliability report could not be loaded."}
        </p>
      </Card>
    );
  }

  if (loading || !report) {
    return (
      <Card title={`Reliability · last ${label}`}>
        <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          Loading…
        </p>
      </Card>
    );
  }

  const total = report.total ?? { polls: 0, failures: 0, failureRate: 0, feeds: 0 };
  const totalTone = reliabilityTone(total.failureRate);
  const noTraffic = total.polls === 0;

  const header = (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>
        Reliability · last {label}
      </p>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
        {/* A window change keeps the previous numbers on screen; saying so is what
            stops a stale report from being read as the new window's answer. */}
        {refreshing ? (
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>updating…</span>
        ) : null}
        <WindowSwitcher windowToken={activeWindow} onWindow={onWindow} />
      </span>
    </div>
  );

  return (
    <>
      <div className="ical-card" aria-busy={refreshing || undefined}>
        {header}
        <p
          style={{
            margin: "10px 0 0",
            fontSize: "var(--text-2xl, 24px)",
            fontWeight: 600,
            color: noTraffic ? "var(--text-secondary)" : totalTone,
          }}
          data-total-share={total.failureRate}
        >
          {noTraffic ? "No requests" : shareLabel(total.failureRate)}
        </p>
        <p style={{ margin: "2px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          {noTraffic
            ? "Nothing was polled in this window, so there is no failure share to show — which is not the same as zero failures."
            : `${total.failures.toLocaleString()} of ${total.polls.toLocaleString()} requests failed, across ${total.feeds.toLocaleString()} calendars.`}
        </p>
        {report.feedsUnmeasurable > 0 ? (
          <p style={{ margin: "8px 0 0", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
            {report.feedsUnmeasurable} of {total.feeds.toLocaleString()} calendars had fewer than{" "}
            {report.minPolls} requests in this window. That is not enough to judge, so they are counted
            in the totals above and left out of the rankings below.
          </p>
        ) : null}
      </div>

      {report.providers.length > 0 ? (
        <Card
          title="By provider"
          subtitle="Is the calendar service failing, or is it one calendar? A provider is ranked on all of its traffic together."
        >
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {report.providers.map((provider) => {
              const tone = reliabilityTone(provider.failureRate);
              return (
                <div
                  key={provider.provider}
                  data-provider={provider.provider}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(90px, 1.2fr) minmax(60px, 1fr) auto",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 500 }}>
                    {provider.provider}
                    <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>
                      {" "}
                      · {provider.feeds} {provider.feeds === 1 ? "calendar" : "calendars"}
                    </span>
                  </span>
                  <ShareBar rate={provider.failureRate} />
                  <Counters
                    polls={provider.polls}
                    failures={provider.failures}
                    rate={provider.failureRate}
                    tone={tone}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      <Card
        title={scope === "admin" ? "Worst calendars across all customers" : "Your worst calendars"}
        subtitle={
          report.feeds.length === 0
            ? undefined
            : `Ranked by failure share, worst first. A calendar needs at least ${report.minPolls} requests in the window to be ranked.`
        }
      >
        {report.feeds.length === 0 ? (
          <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            {noTraffic
              ? "Nothing was polled in this window."
              : `No calendar had at least ${report.minPolls} requests in this window, so none of them can be ranked. The totals above still count every request.`}
          </p>
        ) : (
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {report.feeds.map((feed) => {
              const tone = reliabilityTone(feed.failureRate);
              const row = (
                <>
                  <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={feed.url}
                    >
                      {feed.provider || feed.url || feed.feedId}
                    </span>
                    <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
                      {feed.healthStatus ? `${feed.healthStatus} · ` : ""}
                      {scope === "admin" && feed.projects > 0
                        ? `${feed.projects} ${feed.projects === 1 ? "project" : "projects"}`
                        : feed.url}
                    </span>
                  </span>
                  <ShareBar rate={feed.failureRate} />
                  <Counters
                    polls={feed.polls}
                    failures={feed.failures}
                    rate={feed.failureRate}
                    tone={tone}
                  />
                </>
              );

              const gridStyle = {
                display: "grid",
                gridTemplateColumns: "minmax(120px, 1.4fr) minmax(60px, 1fr) auto",
                alignItems: "center",
                gap: 10,
                textAlign: "left",
                width: "100%",
              };

              // Only a customer can open a feed detail screen; for an
              // administrator the row is a row, not a link to somebody else's
              // calendar.
              return onOpenFeed ? (
                <button
                  key={feed.feedId}
                  type="button"
                  data-feed={feed.feedId}
                  onClick={() => onOpenFeed(feed.feedId)}
                  style={{ ...gridStyle, background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit" }}
                >
                  {row}
                </button>
              ) : (
                <div key={feed.feedId} data-feed={feed.feedId} style={gridStyle}>
                  {row}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}
